/**
 * CariesIntelligenceCard — Phase B Section 3.
 *
 * Educational caries-environment dashboard. Pulls authoritative score from
 * a recent CAMBRA screening when available, otherwise uses the environment
 * estimate. Surfaces favorable / unfavorable factors + opportunities.
 *
 * STRICTLY EDUCATIONAL.
 */
import React from "react";
import { ShieldCheck, AlertTriangle, Lightbulb, ArrowRight } from "lucide-react";
import ScoreExplanation from "@/components/dental_intel/ScoreExplanation";

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
};

const RISK_LABEL = {
  bg: { low: "Нисък", moderate: "Умерен", high: "Висок" },
  en: { low: "Low",   moderate: "Moderate", high: "High" },
  es: { low: "Bajo",  moderate: "Moderado", high: "Alto" },
};

export default function CariesIntelligenceCard({ data, lastScreening, isDark, lang = "bg", cardCls }) {
  const t = {
    bg: {
      title: "Кариес — образователна среда",
      score_label: "Кариес среда",
      risk_label: "Риск",
      cambra_anchor: "Водеща оценка от Вашата проверка по CAMBRA",
      env_estimate: "Оценка от хранителни и метаболитни сигнали (приблизителна)",
      favorable: "Защитни фактори",
      unfavorable: "Натоварващи фактори",
      opportunities: "Възможности за подобрение",
      cta: "Започнете пълната дентална проверка (CAMBRA + EFP) →",
    },
    en: {
      title: "Caries — educational environment",
      score_label: "Caries environment",
      risk_label: "Risk",
      cambra_anchor: "Authoritative score from your CAMBRA screening",
      env_estimate: "Estimate from nutrition & metabolic signals (approximate)",
      favorable: "Protective factors",
      unfavorable: "Burden factors",
      opportunities: "Improvement opportunities",
      cta: "Run full dental screening (CAMBRA + EFP) →",
    },
  }[lang] || {};

  const score = data?.score ?? 0;
  const band = data?.band || "yellow";
  const riskLabel = (RISK_LABEL[lang] || RISK_LABEL.bg)[data?.risk_label] || data?.risk_label;
  const fromScreening = data?.source === "cambra_screening";

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-caries-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-500" />
          {t.title}
        </h3>
        <div className="text-right">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${BAND_PILL[band]}`} data-testid="dhi-caries-score">
            {score}/100 · {riskLabel}
          </span>
        </div>
      </div>
      <p className={`text-[11px] mb-3 ${fromScreening ? "text-emerald-600 dark:text-emerald-300" : "opacity-60"}`}>
        {fromScreening ? `✓ ${t.cambra_anchor}` : t.env_estimate}
        {lastScreening && (
          <span className="ml-1 opacity-60">
            ({new Date(lastScreening.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "bg-BG")})
          </span>
        )}
      </p>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <FactorList icon={ShieldCheck} title={t.favorable} items={data?.favorable} tone="emerald" testid="dhi-caries-favorable" />
        <FactorList icon={AlertTriangle} title={t.unfavorable} items={data?.unfavorable} tone="rose" testid="dhi-caries-unfavorable" />
      </div>

      <div className={`p-3 rounded-lg ${isDark ? "bg-amber-500/10" : "bg-amber-50"}`} data-testid="dhi-caries-opportunities">
        <div className="text-xs font-bold mb-1.5 flex items-center gap-1.5">
          <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
          {t.opportunities}
        </div>
        <ul className="space-y-1 text-xs">
          {(data?.opportunities || []).map((o, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <ArrowRight className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </div>

      <ScoreExplanation
        isDark={isDark}
        lang={lang}
        testid="dhi-caries-why"
        inputs={lang === "en"
          ? "DIL, daily sugar, meal frequency, smoking, alcohol, fiber, protein, vitamin D. If a CAMBRA screening is on file, its 0-10 score is used directly."
          : "DIL, дневна захар, честота на хранене, тютюнопушене, алкохол, фибри, протеин, vit D. Ако имате проверка по CAMBRA, неговият 0-10 скор се ползва директно."}
        formula={lang === "en"
          ? "Without screening: starts at 85, subtracts ~9 per unfavorable factor, adds ~3 per favorable. With CAMBRA screening: score = 100 − (CAMBRA × 10). Bands: ≥75 green, 50-74 yellow, <50 orange/red."
          : "Без проверка: започва от 85, изважда ~9 за всеки натоварващ фактор, добавя ~3 за защитен. С проверка по CAMBRA: оценка = 100 − (CAMBRA × 10). Цветове: ≥75 зелено, 50-74 жълто, <50 оранжево/червено."}
        factors={[
          ...((data?.favorable) || []).slice(0,3).map((f) => `✓ ${f}`),
          ...((data?.unfavorable) || []).slice(0,3).map((f) => `✗ ${f}`),
        ]}
      />
    </div>
  );
}

function FactorList({ icon: Icon, title, items, tone, testid }) {
  const toneCls = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    rose:    "text-rose-600 dark:text-rose-400",
  }[tone];
  return (
    <div data-testid={testid}>
      <div className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${toneCls}`}>
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <ul className="space-y-1 text-xs">
        {items && items.length ? items.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${tone === "emerald" ? "bg-emerald-500" : "bg-rose-500"}`} />
            <span className="opacity-80">{f}</span>
          </li>
        )) : (
          <li className="opacity-40 italic text-[11px]">—</li>
        )}
      </ul>
    </div>
  );
}
